/**
 * Share Details page component module.
 * @module advisor/shared/pages/ShareDetails
 */

import React, {useEffect, useState} from 'react';
import history from '../../../history';
import Promise from 'bluebird';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import lazy from 'lazy.js';

import {Button, Checkbox, Column, DatePicker, DescriptiveText, Dropdown, Page, RadioSet, Row, Table, Tile} from '../../../shared';
import debug from 'debug';
import {_openModal} from '../../../shared/reducers/modals';
import {gatherData, getUpdatedFilter, setSectionURI} from '../../../shared/utils/utils';
import axiosInstance from '../../../shared/utils/axios';
import {_setNotification} from '../../../shared/reducers/headerNotification';

const log = debug('circleblack:share-details');
/**
 * Share Details page component to view document share details.
 * @name ShareDetails
 */
const ShareDetails = props => {
  const {openModal, userProfile, setNotification} = props;
  const compliance = props.history.location.pathname.split('/').filter(el => !!el)[0] === 'compliance';
  const today = moment().format('YYYY-MM-DD');
  const {id} = useParams();
  const gatherShared = gatherData(`/documents/${id}/households?${compliance ? 'compliance=1' : ''}`, true);
  const getDocument = gatherData(`/documents/${id}?${compliance ? 'compliance=1' : ''}`);
  const getDocumentApproval = gatherData(`/documents/${id}?${compliance ? 'compliance=1' : ''}`);
  const defaultParams = {};
  const [document, setDocument] = useState({});
  const [selectShareBtn, setSelectShareBtn] = useState('');
  const [bodyCopy, setBodyCopy] = useState('');
  const [selectedHH, setSelectedHH] = useState([]);
  const [hasAssocHH, setHasAssocHH] = useState(false);
  const [shareSchedule, setShareSchedule] = useState({immediate: 1});
  const [selectedDate, setSelectedDate] = useState(moment().add(1, 'd').format('YYYY-MM-DD'));
  const timeOfDayOptions = [
    {label: 'Morning', value: 'Morning'},
    {label: 'Afternoon', value: 'Afternoon'},
    {label: 'Evening', value: 'Evening'},
  ];

  const shareDataManager = {
    gatherData: inputValue => gatherShared({
      ...getUpdatedFilter(defaultParams),
      ...inputValue,
    })
      .catch(err => {
        log('err thrown');
        openModal({
          type: 'Error',
          title: 'Failed to Retrieve Share Details',
          err: err,
        });
        history.push(setSectionURI());
        throw err;
      }),
    setSelectedRows: (rd) => {
      setSelectedHH(rd);
    },
  };

  const genBodyCopy = () => {
    let bodyText = bodyCopy;
    if (document.shareApproval !== 'DENIED') {
      if (document.assocHousehold) {
        bodyText = `${document.title} is intended for ${document.assocHousehold} and can only be shared with them.`;
      } else if (compliance) {
        bodyText = (<p>The list below shows you who it has already been shared with and who has viewed it. You can also select to
          “Unshare” it from select
          households or from all.</p>);
      } else {
        bodyText = (<p>You can click the “Share” button to send {document.title} to selected or targeted households. The list
          below shows you who it has already been shared with and who has viewed it. You can also select to “Unshare” it from select
          households or from all.</p>);
      }
    } else if (document.shareApproval === 'DENIED') {
      bodyText = (<div><p>You attempted to share this document but it was denied by compliance{document.notes
        ? ':'
        : '.'}</p><p>{document.notes ? '"' + document.notes + '"' : null}</p></div>);
    }
    return setBodyCopy(bodyText);
  };

  const disableOwnerHH = (rowData) => rowData.id === userProfile.id;

  const sharedTable = (
    <Table dataManager={shareDataManager}
           columns={[
             {
               title: 'Household',
               dataPoint: 'name',
               type: 'text',
               minWidth: 600,
               alignment: 'left',
               isSortable: true,
             },
             {
               title: 'Email',
               dataPoint: 'email',
               alignment: 'left',
               type: 'text',
               minWidth: 300,
               isSortable: true,
             },
             {
               title: 'Shared',
               dataPoint: 'state',
               alignment: 'left',
               type: 'text',
               minWidth: 175,
               isSortable: true,
             },
             {
               title: 'Read',
               dataPoint: 'readAt',
               alignment: 'right',
               type: 'date',
               minWidth: 130,
               isSortable: true,
             },
           ]}
           select={{type: 'all', max: 100, rowDisabledFunc: disableOwnerHH}}
           defaultSort={{column: 'name', desc: false}}
           rowIdentifier={'id'}
           height={'750px'}/>
  );

  const selectShareSchedule = <>
    <Column>
      <Row justifySubRow={'left'} width={'100%'}>
        <RadioSet dataPoint={{name: 'schedule', value: shareSchedule.immediate}}
                  options={[
                    {label: 'Immediately (as soon as it is approved)', value: 1},
                    {label: 'Select Date and Time', value: 0},
                  ]}
                  onChange={(name, val) => {
                    let newDate = shareSchedule.distributionDate || moment(selectedDate).toISOString();
                    setShareSchedule(val
                      ? {...shareSchedule, immediate: 1}
                      : {
                        ...shareSchedule,
                        immediate: 0,
                        distributionDate: newDate,
                        distributionTime: shareSchedule.distributionTime || 'Morning',
                      });
                  }}/>
      </Row>
      <Row justifySubRow={'left'}>
        <div>
          <DatePicker title={'Date'}
                      dataPoint={{name: 'date', value: selectedDate}}
                      nimDate={today}
                      onChange={(name, value) => {
                        let newDate = moment(value).toISOString();
                        setShareSchedule({...shareSchedule, distributionDate: newDate});
                        setSelectedDate();
                        setSelectedDate(value);
                      }}
                      disabled={!!shareSchedule.immediate}/>
        </div>
        <div>
          <Dropdown dataPoint={{name: 'timeOfDay'}}
                    title={'Time of Day'}
                    placeholder={'Time of Day'}
                    width={'160px'}
                    defaultValue={timeOfDayOptions[0]}
                    options={timeOfDayOptions}
                    onChange={(name, value) => {
                      setShareSchedule(current => ({...current, distributionTime: value.value}));
                    }}
                    disabled={!!shareSchedule.immediate}/>
        </div>
      </Row>
      <Row justifySubRow={'left'}>
        <Checkbox title={'Mark as High Priority (this is only for compliance approval)'}
                  dataPoint={{name: 'highPriority', value: !!shareSchedule.highPriority || !!0}}
                  onChange={(name, value) => {
                    setShareSchedule({...shareSchedule, highPriority: value});
                  }}
                  width={'400px'}/>
      </Row>
    </Column>
  </>;

  const submitShare = () => {
    const shareBody = {};
    const queryParams = {};

    shareBody.approved = document.approved;
    if (shareSchedule.immediate) {
      shareBody.immediate = shareSchedule.immediate;
    } else {
      shareBody.distributionDate = shareSchedule.distributionDate;
      shareBody.distributionTime = shareSchedule.distributionTime;
    }
    shareBody.highPriority = !!shareSchedule.highPriority;
    shareBody.households = hasAssocHH ? [hasAssocHH] : lazy(selectedHH).pluck('id').toArray();

    axiosInstance.post(`/documents/${document.id}/share`, shareBody, {params: queryParams})
      .then(() => {
        history.push(setSectionURI(`/${document.id}/sharing`));
        setSelectShareBtn('unshare');
        setNotification({
          message: `The ${document.type === 'Article'
            ? 'article'
            : 'document'} has been submitted. Once it is approved it will be sent to the selected households at the chosen time.`,
        });
      })
      .catch((err) => {
        let error = err.response && err.response;
        if (error.data && error.data.error && error.status === 404 && error.data.error.code === 132) {
          openModal({
            type: 'MessageModal',
            title: 'Failed to Submit Share',
            body: 'None of your households matched the targeting criteria you created. Please choose a different set of criteria, or select the individual households.',
          });
        } else if (error.status < 500 && error.data && error.data.error && error.data.error.msg) {
          openModal({
            type: 'MessageModal',
            title: 'Failed to Submit Share',
            body: 'Unfortunately we encountered an error processing the approval. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.',
          });
        } else {
          openModal({
            type: 'MessageModal',
            title: 'Failed to Submit Share',
            body: err.response.data.error.msg,
          });
        }
      });
  };

  useEffect(() => {
    if (!document.id) {
      Promise.join(
        getDocument({includeApproved: true, includeNotShareable: true}),
        getDocumentApproval(),
        gatherShared(),
        (doc, approval, sharedHH) => {
          if (doc.notShareable || doc.cat === 'Report' && !doc.assocHouseholdID) {
            history.push(setSectionURI());
          }
          if (approval.length) {
            doc.shareApproval = approval[0].criteria;
            doc.notes = approval[0].notes;
          }
          genBodyCopy();
          setSelectedHH(sharedHH);
          setHasAssocHH(doc.shareApproval !== 'DENIED' && doc.assocHouseholdID);
          setDocument(doc);
        }
      )
        .catch(function error(err) {
          log('error was caught in useEffect', err);
          let res = err.response;
          if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
            err.message = 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
          }
          openModal({
            type: 'Error',
            title: 'Failed to Retrieve Share related info',
            err: err,
          });
        });
    }
  }, []);

  useEffect(() => {
    if (hasAssocHH && selectedHH.length) {
      setSelectShareBtn('unshare');
    } else if (hasAssocHH && !selectedHH.length) {
      setSelectShareBtn('share');
    } else {
      setSelectShareBtn('');
    }
  }, [hasAssocHH]);

  useEffect(() => {
    if (selectShareBtn === 'unshare' && !hasAssocHH.length) {
      gatherShared()
        .then(res => {
          setSelectedHH(res);
        })
        .catch(err => openModal({
          type: 'Error',
          title: 'Failed to Retrieve Share related info',
          err: err,
        }));
    }
  }, [selectShareBtn]);

  log('rendering ShareDetails');
  return <Page>
    <Tile key='ShareDetails' title={'Share Details'}>
      <Button importance={'secondary'} size={'md'} label={'Back'} action={() => history.push(setSectionURI())} width={'240px'}/>
      <DescriptiveText>
        {bodyCopy}
      </DescriptiveText>
      <Row wrap={false}>
        {(selectShareBtn !== 'unshare' && !compliance) && <Column width={'400px'}>
          <Button importance={'secondary'}
                  size={'md'}
                  label={'Share'}
                  action={hasAssocHH && submitShare || (() => {
                    history.push(setSectionURI(`/${id}/share`));
                  })}
                  width={'300px'}/>
        </Column>}
        {selectShareBtn !== 'share' && <Column width={'400px'}>
          <Button importance={'secondary'}
                  size={'md'}
                  label={'Unshare'}
                  action={() => openModal({
                    componentName: 'UnshareDocument',
                    selected: selectedHH,
                    document: document,
                    refresh: shareDataManager.refresh || (() => {setSelectShareBtn('share');}),
                  })}
                  disabled={selectedHH && !selectedHH.length}
                  width={'300px'}/>
        </Column>}
      </Row>
      {selectShareBtn === 'share' && !compliance && selectShareSchedule}
      {!selectShareBtn && document.id && sharedTable}
    </Tile>
  </Page>;
};

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setNotification: note => dispatch(_setNotification(note)),
});

const mapStateToProps = state => ({
  userProfile: state.session.profile,
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareDetails);

ShareDetails.propTypes = {
  /**
   * The open function that dispatches _openModal
   */
  openModal: PropTypes.func.isRequired,
  userProfile: PropTypes.object.isRequired,
  /**
   * Notification for share confirmation in header. Takes object with message property.
   */
  setNotification: PropTypes.func.isRequired,
  /**
   * The state passed in, in this case if compliance.
   */
  history: PropTypes.func,
};
